import { useCallback, useEffect, useState } from "react";
import {
  getFirestore,
  collection,
  query,
  where,
  getCountFromServer,
} from "firebase/firestore";
import acompanhamentoApi from "../../service/modules/acompanhamento.api";
import useRecargaPeriodica from "../../util/freight/useRecargaPeriodica";
import {
  comAlerta,
  diasDeAtraso,
  STATUS_EM_TRANSITO,
  STATUS_ENTREGUE,
  STATUS_FINALIZADO,
} from "../../util/freight/acompanhamento-marcacoes";

const db = getFirestore();

/**
 * Teto do cálculo de alerta. O alerta vem avaliado do backend, mas por frete — então contar exige os
 * documentos e não sai de `getCountFromServer`. Em homologação a frota inteira em trânsito são 66
 * fretes, e o maior embarcador tem 57, então uma requisição cobre a base de hoje. Acima disso o
 * número vira "pelo menos", e é isso que o card diz.
 */
const TETO_PARA_ALERTA = 100;

const RECARGA_DOS_CARDS_MS = 5 * 60 * 1000;

const LIMITE_DO_IN = 30;

function consultaDeContagem(status, uids) {
  const partes = [collection(db, "freight")];
  if (status) partes.push(where("status.describe", "==", status));
  if (uids?.length === 1) {
    partes.push(where("shipper.uid", "==", uids[0]));
  } else if (uids?.length) {
    partes.push(where("shipper.uid", "in", uids));
  }
  return query(...partes);
}

/**
 * Contagem no servidor: uma leitura por 1.000 entradas de índice, contra os documentos inteiros que
 * a tela antiga baixava só para preencher um número. `status` opcional — sem ele conta o escopo todo.
 * O `in` do Firestore cabe 30 uids; transportadora afiliada pode passar disso, e a soma junta os pedaços.
 */
export async function contarFretes(escopo, status) {
  const uids = escopo.viewALL ? null : escopo.uids || [];
  if (!uids || uids.length <= LIMITE_DO_IN) {
    return getCountFromServer(consultaDeContagem(status, uids));
  }

  let count = 0;
  for (let i = 0; i < uids.length; i += LIMITE_DO_IN) {
    const snap = await getCountFromServer(
      consultaDeContagem(status, uids.slice(i, i + LIMITE_DO_IN)),
    );
    count += snap.data().count;
  }
  return { data: () => ({ count }) };
}

export default function useCards(escopo) {
  const [cards, setCards] = useState({
    emTransito: 0,
    comAlerta: 0,
    emAtraso: 0,
    entregues: 0,
    finalizadas: 0,
    alertaParcial: false,
  });

  const carregar = useCallback(async () => {
    if (!escopo.viewALL && !escopo.uids.length) return;

    try {
      const [transito, entregues, finalizadas] = await Promise.all([
        contarFretes(escopo, STATUS_EM_TRANSITO),
        contarFretes(escopo, STATUS_ENTREGUE),
        contarFretes(escopo, STATUS_FINALIZADO),
      ]);

      // Atraso sai da previsão de entrega; alerta vem avaliado pelo backend, com hora do servidor.
      // Os dois sobre a frota em trânsito, que é o único recorte em que fazem sentido.
      const emTransitoResposta = await acompanhamentoApi.listarFretes({
        ...escopo.params,
        status: STATUS_EM_TRANSITO,
        limit: TETO_PARA_ALERTA,
      });
      const fretes = emTransitoResposta?.data?.data ?? [];

      setCards({
        emTransito: transito.data().count,
        entregues: entregues.data().count,
        finalizadas: finalizadas.data().count,
        emAtraso: fretes.filter((f) => diasDeAtraso(f) > 0).length,
        comAlerta: fretes.filter(comAlerta).length,
        alertaParcial: Boolean(emTransitoResposta?.data?.pagination?.hasMore),
      });
    } catch (erro) {
      console.error("Erro ao carregar os cards de acompanhamento:", erro);
    }
  }, [escopo]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // Cada leitura dos cards soma centenas de documentos — a de ocorrência lê a frota em trânsito
  // inteira —, então eles se renovam de 5 em 5 min, e não no ritmo da lista.
  useRecargaPeriodica(carregar, RECARGA_DOS_CARDS_MS);

  return cards;
}
